import { useDateTimeFormatter } from "../../../settings/format-date-time.js";
import { AdminTimeZoneLabel } from "../AdminTimeZoneLabel.js";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Button,
  ConfirmDialog,
  DescriptionItem,
  DescriptionList,
  Skeleton,
  StatusPill,
} from "@playbookai/ui";
import {
  createIntegrationAdminApi,
  createIntegrationConnectionsApi,
  useApiClient,
  type IntegrationReadiness,
  type TenantIntegrationDetail,
} from "@playbookai/api-clients";
import { useConnectionAuthorization } from "../../settings/use-connection-authorization.js";
import {
  ADMIN_INTEGRATIONS_ROUTE,
  adminIntegrationSectionRoute,
} from "../../../routes.js";

/** Host-side shared setup surface. Every fact comes from the current actor's server snapshot. */
export function IntegrationSetup({
  instance,
  children,
}: {
  instance: TenantIntegrationDetail;
  children?: ReactNode;
}) {
  const config = useApiClient();
  const formatter = useDateTimeFormatter();
  const { dateTime } = formatter;
  const admin = useMemo(() => createIntegrationAdminApi(config), [config]);
  const accounts = useMemo(
    () => createIntegrationConnectionsApi(config),
    [config],
  );
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [confirmation, setConfirmation] = useState<{
    enabled: boolean;
    snapshot: IntegrationReadiness;
  } | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleted, setDeleted] = useState<{ cleanupStatus: string } | null>(
    null,
  );
  const readiness = useQuery({
    queryKey: ["integration-readiness", instance.id],
    queryFn: () => admin.readiness(instance.id),
    enabled: !deleted,
  });
  const mine = useQuery({
    queryKey: ["integration-connections"],
    queryFn: () => accounts.listMine(),
    enabled: !deleted,
  });
  const attempt = useQuery({
    queryKey: ["integration-authorization-attempt", instance.id],
    queryFn: () => accounts.authorizationAttempt(instance.id),
    enabled: !deleted,
    refetchInterval: (q) =>
      ["pending", "processing"].includes(q.state.data?.outcome ?? "")
        ? 3_000
        : false,
  });
  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["integration-connections"] }),
      queryClient.invalidateQueries({
        queryKey: ["integration-readiness", instance.id],
      }),
      queryClient.invalidateQueries({
        queryKey: ["integration-authorization-attempt", instance.id],
      }),
      queryClient.invalidateQueries({
        queryKey: ["integration-admin-connections", instance.id],
      }),
    ]);
  };
  useEffect(() => {
    if (
      attempt.data?.outcome === "connected" ||
      attempt.data?.outcome === "failed"
    ) {
      void queryClient.invalidateQueries({
        queryKey: ["integration-connections"],
      });
      void queryClient.invalidateQueries({
        queryKey: ["integration-readiness", instance.id],
      });
    }
  }, [attempt.data?.outcome, instance.id, queryClient]);
  const authorization = useConnectionAuthorization(accounts, refresh);
  const cancel = useMutation({
    mutationFn: () =>
      accounts.cancelAuthorization(instance.id, attempt.data!.attemptId),
    onSettled: refresh,
  });
  const enable = useMutation({
    mutationFn: () =>
      admin.setEnabled(
        instance.id,
        confirmation!.enabled,
        confirmation!.snapshot.observationToken,
      ),
    onSuccess: async (updated) => {
      queryClient.setQueryData(["tenant-integration", instance.id], updated);
      setConfirmation(null);
      await queryClient.invalidateQueries({
        queryKey: ["tenant-integrations"],
      });
    },
    onSettled: refresh,
  });
  const preview = useMutation({
    mutationFn: async (enabled: boolean) => ({
      enabled,
      snapshot: await admin.readiness(instance.id),
    }),
    onSuccess: setConfirmation,
  });
  const impact = useQuery({
    queryKey: ["integration-deletion-impact", instance.id],
    queryFn: () => admin.deletionImpact(instance.id),
    enabled: deleteOpen,
  });
  const remove = useMutation({
    mutationFn: () =>
      admin.deleteIntegration(instance.id, impact.data!.observationToken),
    onSuccess: async (result) => {
      setDeleted(result);
      setDeleteOpen(false);
      await queryClient.invalidateQueries({
        queryKey: ["tenant-integrations"],
      });
      await queryClient.invalidateQueries({
        queryKey: ["integration-connections"],
      });
    },
    onError: () => {
      void impact.refetch();
    },
  });
  const cleanup = useMutation({
    mutationFn: () => admin.retryCleanup(instance.id),
    onSuccess: (result) => setDeleted(result),
  });
  const pending =
    authorization.isPending ||
    ["pending", "processing"].includes(attempt.data?.outcome ?? "");
  const own = (mine.data ?? []).filter(
    (c) => c.tenantIntegrationId === instance.id && c.subjectType === "user",
  );
  const state = readiness.data;

  if (deleted)
    return (
      <Alert tone="info" title="Integration deleted">
        <p>
          Workspace access and all attached accounts have been revoked.
          Execution and audit history are retained.
        </p>
        {deleted.cleanupStatus === "pending" ? (
          <p>
            Credential cleanup is pending and will retry automatically.{" "}
            <Button
              variant="secondary"
              loading={cleanup.isPending}
              onClick={() => cleanup.mutate()}
            >
              Retry cleanup
            </Button>
          </p>
        ) : deleted.cleanupStatus === "complete" ? (
          <p>Credential cleanup is complete.</p>
        ) : (
          <p>Credential cleanup needs attention. Check its current status.</p>
        )}
        {cleanup.isError ? (
          <p role="alert">Credential cleanup could not finish. Please retry.</p>
        ) : null}
        <Button onClick={() => navigate(ADMIN_INTEGRATIONS_ROUTE)}>
          Back to integrations
        </Button>
      </Alert>
    );
  return (
    <div className="space-y-5">
      {!instance.enabled ? (
        <Alert tone="info" title="Workspace access is off">
          Admins can connect and review tools. Members cannot run tools yet.
        </Alert>
      ) : null}
      <Button
        variant="ghost"
        className="@min-[42.5rem]/settings-page:hidden"
        onClick={() =>
          document.getElementById(`readiness-${instance.id}`)?.focus()
        }
      >
        Jump to setup readiness
      </Button>
      <div className="grid min-w-0 gap-5 @min-[42.5rem]/settings-page:grid-cols-[minmax(0,1fr)_20rem]">
        <div className="min-w-0 space-y-5">
          <section
            aria-label="Your account"
            className="space-y-3 rounded-xl border border-pb-border bg-pb-card p-5"
          >
            <div className="flex flex-wrap items-center gap-3">
              <h3 className="font-semibold">Your account</h3>
              <StatusPill
                tone={state?.ownConnection === "active" ? "healthy" : "neutral"}
              >
                {state?.ownConnection ?? "Checking account"}
              </StatusPill>
            </div>
            <p className="text-sm text-pb-text-muted">
              Only your own credentials are used for discovery. Reconnecting
              affects your account only.
            </p>
            {mine.isError ? (
              <Alert tone="critical" title="Could not load your accounts">
                Check status to retry.
              </Alert>
            ) : (
              own.map((c) => (
                <p className="text-sm" key={c.id}>
                  {c.externalPrincipalName ?? c.displayName} · {c.status}
                </p>
              ))
            )}
            {state?.authenticationSetup === "unavailable" ? (
              <Alert
                tone="watch"
                title="Sign-in is not configured for this deployment"
              >
                Ask an operator to check the OAuth application and callback
                URLs.
              </Alert>
            ) : null}
            {pending ? (
              <p role="status" className="text-sm">
                Sign-in is pending. Complete consent or MFA in the provider tab,
                then check status. Closing the tab does not connect your
                account.
              </p>
            ) : attempt.data &&
              ["failed", "cancelled", "expired"].includes(
                attempt.data.outcome,
              ) ? (
              <p role="status" className="text-sm">
                Sign-in {attempt.data.outcome}. Your saved configuration and
                tool reviews are retained.
              </p>
            ) : null}
            {authorization.error ||
            authorization.windowError ||
            attempt.isError ||
            cancel.isError ? (
              <Alert tone="critical" title="Sign-in needs attention">
                {authorization.windowError ??
                  "Check status to reconcile the previous attempt before trying again."}
              </Alert>
            ) : null}
            <div className="flex flex-wrap gap-2">
              <Button
                disabled={
                  pending ||
                  !state ||
                  state.authenticationSetup !== "ready" ||
                  attempt.isError
                }
                onClick={() => authorization.start(instance.id)}
              >
                {own.length ? "Reconnect your account" : "Connect your account"}
              </Button>
              <Button variant="secondary" onClick={() => void refresh()}>
                Check status
              </Button>
              {pending && authorization.canReopen ? (
                <Button variant="secondary" onClick={authorization.reopen}>
                  Open pending sign-in
                </Button>
              ) : null}
              {pending && attempt.data ? (
                <Button
                  variant="ghost"
                  loading={cancel.isPending}
                  onClick={() => cancel.mutate()}
                >
                  Cancel sign-in
                </Button>
              ) : null}
            </div>
          </section>
          {state?.catalogueRefreshFailed ? (
            <Alert tone="watch" title="Last catalogue refresh failed">
              The last saved catalogue and approvals are retained. Check your
              account and fetch again.
            </Alert>
          ) : null}
          {children}
        </div>
        <section
          id={`readiness-${instance.id}`}
          tabIndex={-1}
          aria-label="Setup readiness"
          className="h-fit space-y-4 rounded-xl border border-pb-border bg-pb-card p-5 focus-ring"
        >
          <h3 className="font-semibold">Setup readiness</h3>
          {readiness.isPending ? <Skeleton className="h-32 w-full" /> : null}
          {readiness.isError ? (
            <Alert tone="critical" title="Could not check readiness">
              Enablement is unavailable until status can be checked.{" "}
              <Button variant="secondary" onClick={() => void refresh()}>
                Retry
              </Button>
            </Alert>
          ) : null}
          {state ? (
            <>
              {/* Look row OK-25: the readiness facts are the DescriptionList. */}
              <DescriptionList>
                <DescriptionItem label="Configuration">
                  <span className="text-pb-text-muted">
                    {state.configuration}
                  </span>
                </DescriptionItem>
                <DescriptionItem label="Your account">
                  <span className="text-pb-text-muted">
                    {state.ownConnection}
                  </span>
                </DescriptionItem>
                {state.catalogue !== "not_applicable" ? (
                  <>
                    <DescriptionItem label="Tool catalogue">
                      <span className="text-pb-text-muted">
                        {state.catalogue.replaceAll("_", " ")}
                        {state.catalogueFetchedAt ? (
                          <>
                            {" · "}
                            <AdminTimeZoneLabel
                              label={dateTime(state.catalogueFetchedAt)}
                              formatter={formatter}
                              variant="inline"
                            />
                          </>
                        ) : null}
                      </span>
                    </DescriptionItem>
                    <DescriptionItem label="Approved tools">
                      {state.approvedToolCount ?? "Unknown"}
                    </DescriptionItem>
                  </>
                ) : null}
              </DescriptionList>
              {state.blockers.map((b) => (
                <p className="text-sm text-pb-text-muted" key={b.code}>
                  {b.safeMessage}
                  {b.action === "catalogue" ? (
                    <Button
                      variant="ghost"
                      onClick={() =>
                        navigate(
                          adminIntegrationSectionRoute(
                            instance.id,
                            "tool-access",
                          ),
                        )
                      }
                    >
                      Open tool access
                    </Button>
                  ) : null}
                </p>
              ))}
              <Button
                className="w-full"
                disabled={
                  !instance.enabled && (!state.canEnable || readiness.isError)
                }
                loading={preview.isPending}
                onClick={() => preview.mutate(!instance.enabled)}
              >
                {instance.enabled
                  ? "Disable workspace access"
                  : "Enable integration"}
              </Button>
              <p className="text-xs text-pb-text-muted">
                Enabling allows granted members to use approved tools with their
                own accounts.
              </p>
            </>
          ) : null}
          {preview.isError ? (
            <Alert tone="critical" title="Could not check current readiness">
              Check status and try again.
            </Alert>
          ) : null}
          <Button
            variant="ghost"
            onClick={() =>
              navigate(adminIntegrationSectionRoute(instance.id, "people"))
            }
          >
            Manage people and access
          </Button>
          {!instance.enabled ? (
            <Button
              variant="secondary"
              onClick={() => {
                remove.reset();
                setDeleteOpen(true);
              }}
            >
              Delete integration
            </Button>
          ) : null}
        </section>
      </div>
      {/* ConfirmDialog (d2): configuration, accounts and history are retained
          either way, so the neutral tone. */}
      <ConfirmDialog
        open={confirmation !== null}
        onClose={() => setConfirmation(null)}
        title={
          confirmation?.enabled
            ? "Enable workspace access?"
            : "Disable workspace access?"
        }
        tone="neutral"
        confirmLabel="Confirm"
        loading={enable.isPending}
        confirmDisabled={
          confirmation?.enabled === true && !confirmation.snapshot.canEnable
        }
        onConfirm={() => enable.mutate()}
      >
        <p>
          {confirmation?.enabled
            ? "Granted members may use approved tools with their own accounts. Unreviewed tools remain unavailable."
            : "New work will stop. Configuration, accounts, approvals and history are retained."}
        </p>
        {enable.isError ? (
          <Alert tone="critical" title="Workspace access was not changed">
            The account or readiness may have changed. Close this confirmation
            and check status before retrying.
          </Alert>
        ) : null}
        {confirmation?.enabled
          ? confirmation.snapshot.blockers.map((b) => (
              <p key={b.code}>{b.safeMessage}</p>
            ))
          : null}
      </ConfirmDialog>
      {/* ConfirmDialog (d2): the destructive confirm, red chip, quiet Cancel. */}
      <ConfirmDialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        title="Delete this integration?"
        confirmLabel="Delete integration"
        loading={remove.isPending}
        confirmDisabled={
          !impact.data?.canDelete ||
          impact.isFetching ||
          impact.isError ||
          remove.isError
        }
        onConfirm={() => remove.mutate()}
      >
        <p>
          This permanently removes the saved integration and revokes every
          attached account. Audit and execution history remain. This cannot be
          undone.
        </p>
        {impact.isPending ? <Skeleton className="h-20 w-full" /> : null}
        {impact.data ? (
          <div className="space-y-3 text-sm">
            <p>
              {impact.data.connectionCount} connections across{" "}
              {impact.data.affectedUserCount} people will be revoked.
            </p>
            {impact.data.enabled ? (
              <p>Disable workspace access before deleting.</p>
            ) : null}
            {impact.data.dependencies.map((d) => (
              <p key={d.id}>Remove the active dependency first: {d.label}</p>
            ))}
          </div>
        ) : null}
        {impact.isError || remove.isError ? (
          <Alert tone="critical" title="Deletion was not confirmed">
            The impact may have changed.{" "}
            <Button
              variant="secondary"
              onClick={() => {
                remove.reset();
                void impact.refetch();
              }}
            >
              Review current impact
            </Button>
          </Alert>
        ) : null}
      </ConfirmDialog>
    </div>
  );
}
